import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Responsive,
  verticalCompactor,
  type ResponsiveLayouts,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  EmptyState,
  IconButton,
  MetricRibbon,
  PageHeader,
  PageLayout,
  Progress,
  buttonStyles,
} from '@lognai/synapse-design-system-v2';
import {
  ArrowDown,
  ArrowUp,
  Boxes,
  CircleAlert,
  DollarSign,
  EllipsisVertical,
  ExternalLink,
  GripVertical,
  Package,
  Pencil,
  Pin,
  Plus,
  PinOff,
  Radar,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldAlert,
  Truck,
  X,
} from 'lucide-react';
import {
  PurchaseOrderStatusBadge,
  SalesOrderStatusBadge,
  buildPurchaseOrderDetailUrl,
  buildSalesOrderDetailUrl,
  formatCurrency,
  formatDate,
  useBackorders,
  usePurchaseOrders,
  useSalesOrderKpis,
  useSalesOrders,
} from '@lognai/plugin-orders-ui';
import { useInventoryKpis } from '@lognai/plugin-inventory-ui';
import { useShipmentKpis } from '@lognai/plugin-shipments-ui';
import { useAllTasks, useWarehouseKpis } from '@lognai/plugin-warehouse-ui';
import { hasPermission } from '@lognai/plugin-sdk';
import { AskLognAIIcon, LatestInsightCard } from '@lognai/plugin-ai-core-ui';
import { RadarSection } from '../components/dashboard/RadarSection';
import { CollaborationCard } from '../components/workspace/CollaborationCard';
import { ChartCardPicker } from '../components/workspace/ChartCardPicker';
import { WorkspaceChartCard } from '../components/workspace/WorkspaceChartCard';
import { CardModuleLink } from '../components/workspace/CardModuleLink';
import { CardHeaderSlotContext } from '../components/workspace/card-header-slot';
import { useAuth } from '../hooks/useAuth';
import {
  useSaveWorkspacePreferences,
  useWorkspacePreferences,
} from '../hooks/useWorkspacePreferences';
import {
  WORKSPACE_CARD_REGISTRY,
  WORKSPACE_GRID_COLUMNS,
  addWorkspaceCard,
  applyWorkspaceGridLayouts,
  chartCardDefinition,
  chartCardIdFor,
  createDefaultWorkspaceCardLayouts,
  isChartCardId,
  moveWorkspaceCard,
  reconcileWorkspaceLayout,
  removeWorkspaceCard,
  serializeWorkspaceLayout,
  toggleWorkspaceCardPin,
  visualIdFromCardId,
  workspaceLayoutsEqual,
  type WorkspaceCardDefinition,
  type WorkspaceBreakpoint,
  type WorkspaceCardId,
  type WorkspaceLayoutState,
} from '../components/workspace/workspace-layout';
import '../components/workspace/workspace-grid.css';

const DEFAULT_EMPTY_LAYOUT: WorkspaceLayoutState = {
  orderedCardIds: [],
  pinnedCardIds: [],
  cardLayouts: createDefaultWorkspaceCardLayouts([]),
};

const WORKSPACE_BREAKPOINTS = { lg: 1024, md: 768, sm: 0 };
const WORKSPACE_COLUMNS = WORKSPACE_GRID_COLUMNS;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const WAREHOUSE_TASK_OVERDUE_DAYS = 7;
const CLOSED_PURCHASE_ORDER_STATUSES = new Set(['received', 'cancelled']);
const CLOSED_BACKORDER_STATUSES = new Set(['fulfilled', 'cancelled']);

function ageInDays(value: Date | string | undefined): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.floor((Date.now() - timestamp) / DAY_IN_MS));
}

function useMobileBreakpoint() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return mobile;
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-6 text-center">
      <CircleAlert className="h-5 w-5 text-warning-foreground" />
      <p className="text-sm font-medium text-foreground">Data unavailable</p>
      <p className="max-w-sm text-xs leading-5 text-muted-foreground">{message}</p>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="space-y-3 p-5" aria-label="Loading">
      <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
      <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-4 w-full animate-pulse rounded bg-muted" />
      <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
    </div>
  );
}

interface WorkspaceCardShellProps {
  definition: WorkspaceCardDefinition;
  editing: boolean;
  hideHeader?: boolean;
  pinned: boolean;
  position: number;
  total: number;
  onMove: (id: WorkspaceCardId, direction: -1 | 1) => void;
  onTogglePin: (id: WorkspaceCardId) => void;
  /** Only chart cards can be removed; built-in cards are permission-driven. */
  onRemove?: (id: WorkspaceCardId) => void;
  children: React.ReactNode;
}

function WorkspaceCardShell({
  definition,
  editing,
  hideHeader = false,
  pinned,
  position,
  total,
  onMove,
  onTogglePin,
  onRemove,
  children,
}: WorkspaceCardShellProps) {
  // Held in state rather than a ref so the body re-renders once the slot exists
  // and its portal actually lands.
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  return (
    <Card
      id={`workspace-card-${definition.id}`}
      className="workspace-card-shell relative flex h-full min-h-0 min-w-0 scroll-mt-24 flex-col overflow-hidden"
    >
      {!hideHeader && (
        <CardHeader
          className={`min-h-[66px] shrink-0 px-4 py-3 ${
            editing ? 'workspace-drag-handle' : ''
          }`}
        >
          <div className="flex min-w-0 items-center gap-3">
            {editing && (
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground"
              >
                <GripVertical className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <CardModuleLink module={definition.module} href={definition.moduleHref} editing={editing} />
                {pinned && <Pin className="h-3 w-3 shrink-0 text-primary" aria-label="Pinned" />}
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <CardTitle className="truncate text-sm">{definition.title}</CardTitle>
                {/* Filled by the card body via CardHeaderSlotContext. */}
                <div ref={setHeaderSlot} className="ml-auto flex shrink-0 items-center gap-1" />
              </div>
            </div>
          </div>

          {editing && (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 px-0"
                  aria-label={`Arrange ${definition.title}`}
                >
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  disabled={position === 0}
                  onSelect={() => onMove(definition.id, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                  Move earlier
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  disabled={position === total - 1}
                  onSelect={() => onMove(definition.id, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                  Move later
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onSelect={() => onTogglePin(definition.id)}>
                  {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                  {pinned ? 'Unpin' : 'Pin to front'}
                </DropdownMenu.Item>
                {onRemove && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      className="text-destructive"
                      onSelect={() => onRemove(definition.id)}
                    >
                      <X className="h-4 w-4" />
                      Remove card
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
        </CardHeader>
      )}
      {hideHeader && editing && (
        <div className="absolute right-2 top-2 z-30 flex items-center rounded-md border border-border bg-surface shadow-sm">
          <span
            className="workspace-drag-handle flex h-8 w-8 cursor-grab items-center justify-center text-muted-foreground"
            aria-label={`Drag ${definition.title}`}
          >
            <GripVertical className="h-4 w-4" />
          </span>
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 px-0"
                aria-label={`Arrange ${definition.title}`}
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item disabled={position === 0} onSelect={() => onMove(definition.id, -1)}>
                <ArrowUp className="h-4 w-4" />
                Move earlier
              </DropdownMenu.Item>
              <DropdownMenu.Item disabled={position === total - 1} onSelect={() => onMove(definition.id, 1)}>
                <ArrowDown className="h-4 w-4" />
                Move later
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onSelect={() => onTogglePin(definition.id)}>
                {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                {pinned ? 'Unpin' : 'Pin to front'}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <CardHeaderSlotContext.Provider value={headerSlot}>{children}</CardHeaderSlotContext.Provider>
      </div>
    </Card>
  );
}

function WorkspaceRadarBody() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(420);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () =>
      setHeight(Math.max(240, container.getBoundingClientRect().height - 45));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full min-h-[240px]">
      <RadarSection height={height} embedded className="h-full" />
    </div>
  );
}

function MiniMetric({
  label,
  value,
  tone = 'neutral',
  askPrompt,
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'neutral' | 'warning' | 'success' | 'primary';
  askPrompt?: string;
}) {
  const toneClass = {
    neutral: 'text-foreground',
    warning: 'text-warning-foreground',
    success: 'text-success-foreground',
    primary: 'text-primary',
  }[tone];
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <p className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </p>
        {askPrompt && <AskLineItemButton label={label} prompt={askPrompt} />}
      </div>
      <p className={`mt-1 truncate text-xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

interface AskPageContext {
  pluginId: string;
  entityType: string;
  entityId: string;
  url: string;
}

function openAskLognAI(prompt: string, pageContext?: AskPageContext) {
  window.dispatchEvent(
    new CustomEvent('lognai:open-ai-chat', { detail: { prompt, pageContext } }),
  );
}

function AskLineItemButton({
  label,
  prompt,
  pageContext,
  className = '',
}: {
  label: string;
  prompt: string;
  pageContext?: AskPageContext;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary transition-colors hover:bg-primary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}
      aria-label={`Ask LognAI about ${label}`}
      title={`Ask LognAI about ${label}`}
      onClick={(event) => {
        event.stopPropagation();
        openAskLognAI(prompt, pageContext);
      }}
    >
      <AskLognAIIcon size={13} aria-hidden="true" />
    </button>
  );
}

export default function WorkspacePage() {
  const navigate = useNavigate();
  const { user, company, activeCompanyId } = useAuth();
  const mobile = useMobileBreakpoint();
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [activeBreakpoint, setActiveBreakpoint] = useState<WorkspaceBreakpoint>('lg');

  const canViewGlobalInsights = hasPermission('global_insights:news:read');
  const canViewSalesOrders = hasPermission('order:sales:read');
  const canViewPurchaseOrders = hasPermission('order:purchase:read');
  const canViewBackorders = hasPermission('order:backorder:read');
  const canViewInventory = hasPermission('inventory:item:read');
  const canViewShipments = hasPermission('shipment:shipment:read');
  const canViewWarehouse = hasPermission('warehouse:warehouse:read');
  const canViewWarehouseTasks = hasPermission('warehouse:task:read');

  const effectivePlugins = useMemo(
    () =>
      new Set(
        company?.effectivePlugins?.length
          ? company.effectivePlugins
          : company?.enabledPlugins ?? []
      ),
    [company?.effectivePlugins, company?.enabledPlugins]
  );

  const visibleDefinitions = useMemo(
    () =>
      WORKSPACE_CARD_REGISTRY.filter((definition) => {
        if (definition.id === 'radar') return canViewShipments || canViewGlobalInsights;
        if (definition.requiredPlugin && !effectivePlugins.has(definition.requiredPlugin)) return false;
        return definition.permissions.every((permission) => hasPermission(permission));
      }),
    [
      canViewGlobalInsights,
      canViewBackorders,
      canViewInventory,
      canViewPurchaseOrders,
      canViewSalesOrders,
      canViewShipments,
      canViewWarehouse,
      canViewWarehouseTasks,
      effectivePlugins,
    ]
  );
  const preferenceQuery = useWorkspacePreferences(user?.id, activeCompanyId);

  // Declared before the card-definition memos below, which read the draft to
  // discover chart cards added in this editing session.
  const [savedLayout, setSavedLayout] = useState<WorkspaceLayoutState>(DEFAULT_EMPTY_LAYOUT);
  const [draftLayout, setDraftLayout] = useState<WorkspaceLayoutState>(DEFAULT_EMPTY_LAYOUT);

  const canAddCharts = hasPermission('analytics:dashboard:read');
  const [chartPickerOpen, setChartPickerOpen] = useState(false);
  /** Titles for pinned charts, learned once each chart executes. */
  const [chartTitles, setChartTitles] = useState<Record<string, string>>({});

  // Chart cards are user-chosen, so they are not in the static registry. Both
  // sources matter: the saved preference (or a saved chart is dropped as an
  // unknown id on the next load) AND the live draft (or a chart just added in
  // edit mode has no definition and renders as nothing until after a save).
  const staticVisibleIds = useMemo(
    () => visibleDefinitions.map((definition) => definition.id),
    [visibleDefinitions]
  );

  /**
   * The card ids a persisted layout is reconciled against.
   *
   * Charts are not in the static registry, so they have to come from the
   * preference being reconciled — and ONLY from it. Feeding in charts that exist
   * only in the unsaved draft makes this set change the moment a chart is added,
   * which re-runs the load effect below; that effect reconciles against the
   * saved preference, which does not have the chart yet, so it appends the chart
   * at the bottom and overwrites the draft mid-edit. It also wrote that result
   * into `savedLayout`, which is what Cancel restores — so the cancelled chart
   * came back.
   */
  const reconcileIdsFor = (saved: { orderedCardIds?: string[] } | null | undefined) => [
    ...staticVisibleIds,
    ...(canAddCharts ? (saved?.orderedCardIds ?? []).filter(isChartCardId) : []),
  ];

  const savedChartCardIds = useMemo(
    () => (preferenceQuery.data?.orderedCardIds ?? []).filter(isChartCardId),
    [preferenceQuery.data]
  );
  const draftChartCardIds = draftLayout.orderedCardIds.filter(isChartCardId);
  const draftChartKey = draftChartCardIds.join('|');

  const visibleIds = useMemo(
    () => [...staticVisibleIds, ...(canAddCharts ? savedChartCardIds : [])],
    [canAddCharts, savedChartCardIds, staticVisibleIds]
  );
  const visibleKey = visibleIds.join('|');

  // Rendering is a separate concern from reconciliation: a chart added in this
  // session has no definition yet but still has to draw, so the draft's charts
  // are included here even though they are kept out of `visibleIds`.
  const definitionById = useMemo(() => {
    const map = new Map(visibleDefinitions.map((definition) => [definition.id, definition]));
    for (const id of [...visibleIds, ...draftChartCardIds]) {
      if (isChartCardId(id) && !map.has(id)) {
        map.set(id, chartCardDefinition(id, chartTitles[visualIdFromCardId(id)]));
      }
    }
    return map;
    // draftChartKey stands in for the array identity, which changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTitles, draftChartKey, visibleDefinitions, visibleIds]);
  const savePreferences = useSaveWorkspacePreferences(user?.id, activeCompanyId);
  const identityKey = `${user?.id ?? 'anonymous'}:${activeCompanyId ?? 'none'}`;

  const [loadedIdentity, setLoadedIdentity] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const previousIdentity = useRef(identityKey);

  useLayoutEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    const measure = () => {
      const measured = container.getBoundingClientRect().width;
      setGridWidth(measured > 0 ? measured : window.innerWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [loadedIdentity, visibleKey]);

  useEffect(() => {
    if (previousIdentity.current === identityKey) return;
    previousIdentity.current = identityKey;
    setLoadedIdentity(null);
    setSavedLayout(DEFAULT_EMPTY_LAYOUT);
    setDraftLayout(DEFAULT_EMPTY_LAYOUT);
    setEditing(false);
    setLoadFailed(false);
    setSaveError(null);
    savePreferences.reset();
  }, [identityKey]);

  useEffect(() => {
    // Never overwrite an edit in progress. A background refetch or a permission
    // change would otherwise discard everything the user has arranged so far.
    if (editing) return;
    if (!user?.id || !activeCompanyId) {
      const fallback = reconcileWorkspaceLayout(null, reconcileIdsFor(null));
      setSavedLayout(fallback);
      setDraftLayout(fallback);
      setLoadedIdentity(identityKey);
      return;
    }
    if (!preferenceQuery.isSuccess && !preferenceQuery.isError) return;

    const source = preferenceQuery.isSuccess ? preferenceQuery.data : null;
    const reconciled = reconcileWorkspaceLayout(source, reconcileIdsFor(source));
    setSavedLayout(reconciled);
    setDraftLayout(reconciled);
    setLoadedIdentity(identityKey);
    setLoadFailed(preferenceQuery.isError);
    setSaveError(null);
    // `editing` is read but deliberately not a dependency: re-running on the
    // true -> false transition would reset the draft from a preference that may
    // not have refetched yet, undoing the save that just closed edit mode.
    // `reconcileIdsFor` is recreated every render and is covered by visibleKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeCompanyId,
    identityKey,
    preferenceQuery.data,
    preferenceQuery.isError,
    preferenceQuery.isSuccess,
    user?.id,
    visibleKey,
  ]);

  const unsaved = loadFailed || !workspaceLayoutsEqual(draftLayout, savedLayout);

  useEffect(() => {
    if (!editing || !unsaved) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const protectLinks = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a');
      if (!anchor || event.defaultPrevented) return;
      if (!window.confirm('Leave Workspace without saving your layout changes?')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', protectLinks, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', protectLinks, true);
    };
  }, [editing, unsaved]);

  const {
    data: orderKpis,
    isLoading: ordersLoading,
    isError: ordersError,
  } = useSalesOrderKpis({ enabled: canViewSalesOrders && effectivePlugins.has('orders') });
  const {
    data: inventoryKpis,
    isLoading: inventoryLoading,
    isError: inventoryError,
  } = useInventoryKpis({ enabled: canViewInventory && effectivePlugins.has('inventory') });
  const {
    data: shipmentKpis,
    isLoading: shipmentLoading,
    isError: shipmentError,
  } = useShipmentKpis({ enabled: canViewShipments && effectivePlugins.has('shipments') });
  const {
    data: warehouseKpis,
    isLoading: warehouseLoading,
    isError: warehouseError,
  } = useWarehouseKpis({ enabled: canViewWarehouse && effectivePlugins.has('warehouse') });
  const {
    data: salesOrdersData,
    isLoading: salesOrdersLoading,
    isError: salesOrdersError,
  } = useSalesOrders(
    { limit: 5, page: 1 },
    { enabled: canViewSalesOrders && effectivePlugins.has('orders') }
  );
  const {
    data: purchaseOrdersData,
    isLoading: purchaseOrdersLoading,
    isError: purchaseOrdersError,
  } = usePurchaseOrders(
    { limit: 5, page: 1 },
    { enabled: canViewPurchaseOrders && effectivePlugins.has('orders') }
  );
  const purchaseOrderRiskEnabled =
    definitionById.has('purchase-orders-at-risk') &&
    canViewPurchaseOrders &&
    effectivePlugins.has('orders');
  const {
    data: purchaseOrderRiskData,
    isLoading: purchaseOrderRiskLoading,
    isError: purchaseOrderRiskError,
  } = usePurchaseOrders(
    { limit: 100, page: 1 },
    { enabled: purchaseOrderRiskEnabled }
  );
  const {
    data: backordersData,
    isLoading: backordersLoading,
    isError: backordersError,
  } = useBackorders(
    { limit: 100, page: 1 },
    {
      enabled:
        purchaseOrderRiskEnabled &&
        canViewBackorders,
    }
  );
  const warehouseTaskAgingEnabled =
    definitionById.has('warehouse-task-aging') &&
    canViewWarehouseTasks &&
    effectivePlugins.has('warehouse');
  const {
    data: warehouseTasksData,
    isLoading: warehouseTasksLoading,
    isError: warehouseTasksError,
  } = useAllTasks(
    { limit: 100, page: 1 },
    { enabled: warehouseTaskAgingEnabled }
  );

  const salesOrders = salesOrdersData?.items ?? [];
  const purchaseOrders = purchaseOrdersData?.items ?? [];
  const purchaseOrdersAtRisk = useMemo(() => {
    const openOrders = (purchaseOrderRiskData?.items ?? []).filter(
      (order) => !CLOSED_PURCHASE_ORDER_STATUSES.has(order.status)
    );
    const rankedByValue = [...openOrders].sort((left, right) => right.total - left.total);
    const highValueCount = rankedByValue.length
      ? Math.max(1, Math.ceil(rankedByValue.length * 0.2))
      : 0;
    const highValueIds = new Set(
      rankedByValue.slice(0, highValueCount).map((order) => order.id)
    );
    const blockedDemandPurchaseOrderIds = new Set(
      (backordersData?.items ?? [])
        .filter(
          (backorder) =>
            backorder.linkedPurchaseOrderId &&
            !CLOSED_BACKORDER_STATUSES.has(backorder.status)
        )
        .map((backorder) => backorder.linkedPurchaseOrderId as string)
    );

    return openOrders
      .map((order) => {
        const overdueDays = order.expectedDeliveryDate
          ? ageInDays(order.expectedDeliveryDate)
          : 0;
        const risks = [
          overdueDays > 0 && { key: 'overdue', label: `${overdueDays}d overdue` },
          !order.expectedDeliveryDate && { key: 'missing-eta', label: 'Missing ETA' },
          highValueIds.has(order.id) && { key: 'high-value', label: 'High value' },
          blockedDemandPurchaseOrderIds.has(order.id) && {
            key: 'blocked-demand',
            label: 'Blocked demand',
          },
        ].filter(Boolean) as Array<{ key: string; label: string }>;
        return { order, overdueDays, risks };
      })
      .filter((item) => item.risks.length > 0)
      .sort(
        (left, right) =>
          Number(right.risks.some((risk) => risk.key === 'blocked-demand')) -
            Number(left.risks.some((risk) => risk.key === 'blocked-demand')) ||
          right.overdueDays - left.overdueDays ||
          right.order.total - left.order.total
      )
      .slice(0, 5);
  }, [backordersData?.items, purchaseOrderRiskData?.items]);
  const warehouseTaskAgingRows = useMemo(() => {
    const labels = {
      receive: 'Receiving',
      pick: 'Picking',
      put_away: 'Putaway',
      transfer: 'Transfers',
    } as const;
    const openTasks = (warehouseTasksData?.items ?? []).filter(
      (task) => task.status !== 'completed' && task.status !== 'cancelled'
    );

    return (Object.keys(labels) as Array<keyof typeof labels>).map((type) => {
      const overdueTasks = openTasks
        .filter(
          (task) =>
            task.type === type &&
            ageInDays(task.createdAt) > WAREHOUSE_TASK_OVERDUE_DAYS
        )
        .sort((left, right) => ageInDays(right.createdAt) - ageInDays(left.createdAt));
      return {
        type,
        label: labels[type],
        count: overdueTasks.length,
        oldestDays: overdueTasks.length ? ageInDays(overdueTasks[0].createdAt) : 0,
      };
    });
  }, [warehouseTasksData?.items]);
  const pendingOrders = orderKpis?.ordersByStatus?.pending ?? 0;
  const warehouseSquareMeters = warehouseKpis?.totalCapacity?.totalSquareMeters ?? 0;
  const usedSquareMeters = warehouseKpis?.totalCapacity?.usedSquareMeters ?? 0;
  const warehouseUtilization =
    warehouseSquareMeters > 0
      ? Math.min(100, Math.round((usedSquareMeters / warehouseSquareMeters) * 100))
      : 0;

  const scrollToCard = (id: WorkspaceCardId) => {
    document.getElementById(`workspace-card-${id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const moveCard = (id: WorkspaceCardId, direction: -1 | 1) => {
    const index = draftLayout.orderedCardIds.indexOf(id);
    setDraftLayout(moveWorkspaceCard(draftLayout, id, index + direction));
    setSaveError(null);
  };

  const addChartCard = (visual: { id: string; title: string }) => {
    setChartTitles((current) => ({ ...current, [visual.id]: visual.title }));
    setDraftLayout(addWorkspaceCard(draftLayout, chartCardIdFor(visual.id)));
    setChartPickerOpen(false);
    setSaveError(null);
  };

  const removeCard = (id: WorkspaceCardId) => {
    setDraftLayout(removeWorkspaceCard(draftLayout, id));
    setSaveError(null);
  };

  const saveLayout = async () => {
    setSaveError(null);
    try {
      const saved = await savePreferences.mutateAsync(serializeWorkspaceLayout(draftLayout));
      // Against the ids in the response, not `visibleIds` — the preference query
      // has not refetched yet, so `visibleIds` still lacks any chart added in
      // this session and reconciling against it would drop the chart just saved.
      const reconciled = reconcileWorkspaceLayout(saved, reconcileIdsFor(saved));
      setSavedLayout(reconciled);
      setDraftLayout(reconciled);
      setLoadFailed(false);
      setEditing(false);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Workspace layout could not be saved'
      );
    }
  };

  const resetDraft = () => {
    if (
      !window.confirm(
        'Reset your Workspace to the default layout? This clears your custom arrangement.'
      )
    ) {
      return;
    }
    // Resets the arrangement, not the user's choice of charts: those are kept
    // and repacked at default size rather than silently deleted.
    setDraftLayout(reconcileWorkspaceLayout(null, [...staticVisibleIds, ...draftChartCardIds]));
    setSaveError(null);
  };

  const cancelEditing = () => {
    setDraftLayout(savedLayout);
    setEditing(false);
    setSaveError(null);
  };

  const layoutReady = loadedIdentity === identityKey;

  const focusItems = [
    definitionById.has('recent-sales-orders') && {
      id: 'orders',
      label: 'Orders',
      value: ordersLoading ? null : ordersError ? 'Unavailable' : orderKpis?.totalOrders.toLocaleString() ?? '0',
      hint: ordersError ? 'Could not load' : `${pendingOrders.toLocaleString()} pending`,
      icon: Package,
      target: 'recent-sales-orders' as WorkspaceCardId,
      warning: pendingOrders > 0,
    },
    definitionById.has('inventory-health') && {
      id: 'inventory',
      label: 'Inventory',
      value: inventoryLoading
        ? null
        : inventoryError
          ? 'Unavailable'
          : inventoryKpis?.totalItems.toLocaleString() ?? '0',
      hint: inventoryError
        ? 'Could not load'
        : `${(inventoryKpis?.itemsBelowReorderPoint ?? 0).toLocaleString()} low stock`,
      icon: Boxes,
      target: 'inventory-health' as WorkspaceCardId,
      warning: (inventoryKpis?.itemsBelowReorderPoint ?? 0) > 0,
    },
    definitionById.has('shipment-flow') && {
      id: 'shipments',
      label: 'Active shipments',
      value: shipmentLoading
        ? null
        : shipmentError
          ? 'Unavailable'
          : shipmentKpis?.inTransitCount.toLocaleString() ?? '0',
      hint: shipmentError
        ? 'Could not load'
        : `${(shipmentKpis?.onHoldCount ?? 0).toLocaleString()} on hold`,
      icon: Truck,
      target: 'shipment-flow' as WorkspaceCardId,
      warning: (shipmentKpis?.onHoldCount ?? 0) > 0,
    },
    definitionById.has('recent-sales-orders') && {
      id: 'revenue',
      label: 'Revenue',
      value: ordersLoading
        ? null
        : ordersError
          ? 'Unavailable'
          : formatCurrency(orderKpis?.totalValue ?? 0),
      hint: ordersError
        ? 'Could not load'
        : `Avg ${formatCurrency(orderKpis?.averageOrderValue ?? 0)} per order`,
      icon: DollarSign,
      target: 'recent-sales-orders' as WorkspaceCardId,
      warning: false,
    },
  ].filter(Boolean) as Array<{
    id: string;
    label: string;
    value: string | null;
    hint: string;
    icon: React.ComponentType<{ className?: string }>;
    target: WorkspaceCardId;
    warning: boolean;
  }>;

  const renderCardBody = (id: WorkspaceCardId) => {
    // Chart cards are dynamic, so they are matched before the static switch.
    if (isChartCardId(id)) {
      const visualId = visualIdFromCardId(id);
      return (
        <WorkspaceChartCard
          visualId={visualId}
          onTitle={(title) =>
            setChartTitles((current) =>
              current[visualId] === title ? current : { ...current, [visualId]: title }
            )
          }
        />
      );
    }

    switch (id) {
      case 'radar':
        return (
          <div className="flex h-full min-h-0 flex-col">
            {/* The radar hides the shell header, so it carries its own module
                eyebrow — and it links out the same way every other card's does. */}
            <div className="shrink-0 px-4 pb-1 pt-3">
              <CardModuleLink module="Operations" href="/shipments/radar" editing={editing} />
            </div>
            <div className="min-h-0 flex-1">
              <WorkspaceRadarBody />
            </div>
          </div>
        );

      case 'collaboration':
        return <CollaborationCard />;

      case 'inventory-health':
        if (inventoryLoading) return <CardSkeleton />;
        if (inventoryError || !inventoryKpis) {
          return <ErrorState message="Inventory health could not be loaded. Existing values are not shown as zero." />;
        }
        return (
          <CardContent className="space-y-5 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              <MiniMetric
                label="Available units"
                value={inventoryKpis.totalQuantityAvailable.toLocaleString()}
                tone="primary"
                askPrompt={`Explain our ${inventoryKpis.totalQuantityAvailable.toLocaleString()} available inventory units. Which SKUs and locations drive this number, and what needs attention?`}
              />
              <MiniMetric
                label="Reserved units"
                value={inventoryKpis.totalQuantityReserved.toLocaleString()}
                askPrompt={`Explain why ${inventoryKpis.totalQuantityReserved.toLocaleString()} inventory units are reserved. Show the orders, SKUs, and customers creating the largest reservations.`}
              />
              <MiniMetric
                label="Below reorder"
                value={inventoryKpis.itemsBelowReorderPoint.toLocaleString()}
                tone={inventoryKpis.itemsBelowReorderPoint > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${inventoryKpis.itemsBelowReorderPoint.toLocaleString()} SKUs below reorder point. Rank them by operational risk and recommend which ones need action first.`}
              />
              <MiniMetric
                label="Out of stock"
                value={inventoryKpis.itemsOutOfStock.toLocaleString()}
                tone={inventoryKpis.itemsOutOfStock > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${inventoryKpis.itemsOutOfStock.toLocaleString()} out-of-stock SKUs. Explain affected demand, customers, and inbound supply.`}
              />
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">Inventory value</p>
                <AskLineItemButton
                  label="inventory value"
                  prompt={`Explain the current inventory value of ${formatCurrency(inventoryKpis.totalValue)}. Show which products and warehouses hold the most value and identify slow-moving or exposed stock.`}
                />
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {formatCurrency(inventoryKpis.totalValue)}
              </p>
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/inventory" className="text-xs font-medium text-primary hover:underline">
                View all inventory
              </Link>
            </div>
          </CardContent>
        );

      case 'recent-sales-orders':
        return (
          <CardContent className="p-0">
            {salesOrdersLoading ? (
              <CardSkeleton />
            ) : salesOrdersError ? (
              <ErrorState message="Recent sales orders could not be loaded." />
            ) : salesOrders.length === 0 ? (
              <EmptyState compact title="No sales orders yet" description="Recent sales orders will appear here." />
            ) : (
              <ul className="divide-y divide-border">
                {salesOrders.map((order) => (
                  <li key={order.id} className="flex items-center transition-colors hover:bg-muted/60">
                    <button
                      type="button"
                      onClick={() => navigate(buildSalesOrderDetailUrl(order.id))}
                      className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{order.orderNumber}</span>
                          <SalesOrderStatusBadge status={order.status} />
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{order.customerName}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums">{formatCurrency(order.total, order.currency)}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{formatDate(order.orderDate)}</p>
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`sales order ${order.orderNumber}`}
                      prompt={`Analyze sales order ${order.orderNumber}. Use the live order, stock, fulfillment, and backorder records to explain its current status, blockers, risks, and next best action.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'salesOrder',
                        entityId: order.id,
                        url: buildSalesOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-border px-4 py-2.5">
              <Link to="/orders/sales" className="text-xs font-medium text-primary hover:underline">View all sales orders</Link>
            </div>
          </CardContent>
        );

      case 'recent-purchase-orders':
        return (
          <CardContent className="p-0">
            {purchaseOrdersLoading ? (
              <CardSkeleton />
            ) : purchaseOrdersError ? (
              <ErrorState message="Recent purchase orders could not be loaded." />
            ) : purchaseOrders.length === 0 ? (
              <EmptyState compact title="No purchase orders yet" description="Recent purchase orders will appear here." />
            ) : (
              <ul className="divide-y divide-border">
                {purchaseOrders.map((order) => (
                  <li key={order.id} className="flex items-center transition-colors hover:bg-muted/60">
                    <button
                      type="button"
                      onClick={() => navigate(buildPurchaseOrderDetailUrl(order.id))}
                      className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{order.orderNumber}</span>
                          <PurchaseOrderStatusBadge status={order.status} />
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {order.supplierName}{order.warehouseName ? ` · ${order.warehouseName}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums">{formatCurrency(order.total, order.currency)}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {order.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : 'No ETA'}
                        </p>
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`purchase order ${order.orderNumber}`}
                      prompt={`Analyze purchase order ${order.orderNumber}. Use the live purchase order and line-item records to explain receipt progress, delay risk, affected demand where supported, and the next supplier action.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'purchaseOrder',
                        entityId: order.id,
                        url: buildPurchaseOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-border px-4 py-2.5">
              <Link to="/orders/purchase" className="text-xs font-medium text-primary hover:underline">View all purchase orders</Link>
            </div>
          </CardContent>
        );

      case 'purchase-orders-at-risk':
        return (
          <CardContent className="p-0">
            {purchaseOrderRiskLoading || (canViewBackorders && backordersLoading) ? (
              <CardSkeleton />
            ) : purchaseOrderRiskError ? (
              <ErrorState message="Purchase-order risk could not be loaded. Existing orders are not shown as safe." />
            ) : purchaseOrdersAtRisk.length === 0 ? (
              <EmptyState
                compact
                title="No open purchase-order risks"
                description="No overdue, missing-ETA, high-value, or demand-linked purchase orders were found."
              />
            ) : (
              <ul className="divide-y divide-border">
                {purchaseOrdersAtRisk.map(({ order, risks }) => (
                  <li key={order.id} className="flex items-center transition-colors hover:bg-muted/60">
                    <button
                      type="button"
                      onClick={() => navigate(buildPurchaseOrderDetailUrl(order.id))}
                      className="min-w-0 flex-1 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="flex min-w-0 items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{order.orderNumber}</p>
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {order.supplierName}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold tabular-nums">
                          {formatCurrency(order.total, order.currency)}
                        </p>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {risks.map((risk) => (
                          <Badge
                            key={risk.key}
                            tone={risk.key === 'blocked-demand' || risk.key === 'overdue' ? 'warning' : 'neutral'}
                            variant="subtle"
                            size="sm"
                          >
                            {risk.label}
                          </Badge>
                        ))}
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`purchase-order risk ${order.orderNumber}`}
                      prompt={`Analyze why purchase order ${order.orderNumber} is at risk. Its current signals are ${risks.map((risk) => risk.label).join(', ')}. Explain the supplier, ETA, value, blocked customer demand, and next best action using live records.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'purchaseOrder',
                        entityId: order.id,
                        url: buildPurchaseOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-1 border-t border-border px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                High value is the top 20% of open PO value.
              </p>
              {canViewBackorders ? (
                backordersError && (
                  <p className="text-xs text-warning-foreground">
                    Blocked-demand links are temporarily unavailable.
                  </p>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  Backorder access is required to assess blocked demand.
                </p>
              )}
            </div>
          </CardContent>
        );

      case 'shipment-flow':
        if (shipmentLoading) return <CardSkeleton />;
        if (shipmentError || !shipmentKpis) {
          return <ErrorState message="Shipment status could not be loaded. Existing values are not shown as zero." />;
        }
        return (
          <CardContent className="space-y-5 p-4">
            <div className="grid grid-cols-2 gap-5">
              <MiniMetric
                label="In transit"
                value={shipmentKpis.inTransitCount.toLocaleString()}
                tone="primary"
                askPrompt={`Review the ${shipmentKpis.inTransitCount.toLocaleString()} shipments currently in transit. Identify late, high-priority, or customer-critical shipments and explain what needs a decision.`}
              />
              <MiniMetric
                label="Booked"
                value={shipmentKpis.bookedCount.toLocaleString()}
                askPrompt={`Review the ${shipmentKpis.bookedCount.toLocaleString()} booked shipments. Identify departures at risk, missing milestones, and the next operational actions.`}
              />
              <MiniMetric
                label="Arrived"
                value={shipmentKpis.arrivedCount.toLocaleString()}
                tone="success"
                askPrompt={`Review the ${shipmentKpis.arrivedCount.toLocaleString()} arrived shipments. Show which still need customs, warehouse receipt, or final delivery action.`}
              />
              <MiniMetric
                label="On hold"
                value={shipmentKpis.onHoldCount.toLocaleString()}
                tone={shipmentKpis.onHoldCount > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${shipmentKpis.onHoldCount.toLocaleString()} shipments on hold. Explain each blocker, business impact, and recommended resolution order.`}
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>Completed or delivered</span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">
                    {(shipmentKpis.completedCount + shipmentKpis.deliveredCount).toLocaleString()} / {shipmentKpis.totalShipments.toLocaleString()}
                  </span>
                  <AskLineItemButton
                    label="shipment completion"
                    prompt={`Explain why ${(shipmentKpis.completedCount + shipmentKpis.deliveredCount).toLocaleString()} of ${shipmentKpis.totalShipments.toLocaleString()} shipments are completed or delivered. Break down the remaining pipeline and its biggest blockers.`}
                  />
                </span>
              </div>
              <Progress
                value={
                  shipmentKpis.totalShipments > 0
                    ? ((shipmentKpis.completedCount + shipmentKpis.deliveredCount) / shipmentKpis.totalShipments) * 100
                    : 0
                }
              />
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/shipments" className="text-xs font-medium text-primary hover:underline">
                View all shipments
              </Link>
            </div>
          </CardContent>
        );

      case 'warehouse-capacity':
        if (warehouseLoading) return <CardSkeleton />;
        if (warehouseError || !warehouseKpis) {
          return <ErrorState message="Warehouse capacity could not be loaded. Existing values are not shown as zero." />;
        }
        return (
          <CardContent className="space-y-5 p-4">
            <div className="grid grid-cols-2 gap-5">
              <MiniMetric
                label="Active warehouses"
                value={`${warehouseKpis.activeWarehouses} / ${warehouseKpis.totalWarehouses}`}
                tone="primary"
                askPrompt={`Review our ${warehouseKpis.activeWarehouses} active warehouses out of ${warehouseKpis.totalWarehouses}. Explain inactive locations, workload concentration, and operational risks.`}
              />
              <MiniMetric
                label="Utilization"
                value={`${warehouseUtilization}%`}
                tone={warehouseUtilization >= 90 ? 'warning' : 'success'}
                askPrompt={`Explain the current warehouse utilization of ${warehouseUtilization}%. Rank locations by capacity pressure and recommend balancing actions.`}
              />
              <MiniMetric
                label="Used space"
                value={`${usedSquareMeters.toLocaleString()} m²`}
                askPrompt={`Explain the ${usedSquareMeters.toLocaleString()} square metres of used warehouse space. Show where space is concentrated and which inventory drives it.`}
              />
              <MiniMetric
                label="Pallet positions"
                value={(warehouseKpis.totalCapacity.usedPalletPositions ?? 0).toLocaleString()}
                askPrompt={`Explain the ${(warehouseKpis.totalCapacity.usedPalletPositions ?? 0).toLocaleString()} used pallet positions. Identify warehouses nearing capacity and available alternatives.`}
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>Square-metre capacity</span>
                <span className="flex items-center gap-2">
                  <span>{warehouseSquareMeters.toLocaleString()} m²</span>
                  <AskLineItemButton
                    label="warehouse capacity"
                    prompt={`Analyze total warehouse capacity of ${warehouseSquareMeters.toLocaleString()} square metres at ${warehouseUtilization}% utilization. Identify capacity risks and recommended transfers.`}
                  />
                </span>
              </div>
              <Progress value={warehouseUtilization} />
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/warehouse" className="text-xs font-medium text-primary hover:underline">
                View all warehouses
              </Link>
            </div>
          </CardContent>
        );

      case 'warehouse-task-aging':
        if (warehouseTasksLoading) return <CardSkeleton />;
        if (warehouseTasksError || !warehouseTasksData) {
          return <ErrorState message="Warehouse task aging could not be loaded. Existing tasks are not shown as current." />;
        }
        return (
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {warehouseTaskAgingRows.map((row) => (
                <li key={row.type} className="flex min-h-[58px] items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{row.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {row.count === 0
                        ? 'No overdue open tasks'
                        : `Oldest open task: ${row.oldestDays} days`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-lg font-semibold tabular-nums ${
                      row.count > 0 ? 'text-warning-foreground' : 'text-foreground'
                    }`}
                  >
                    {row.count}
                  </span>
                  <AskLineItemButton
                    label={`overdue ${row.type === 'transfer' ? 'transfer' : row.label.toLowerCase()} tasks`}
                    prompt={`Investigate the ${row.count} overdue ${row.type === 'transfer' ? 'transfer' : row.label.toLowerCase()} warehouse tasks. Explain the oldest tasks, warehouses, owners, blocked work, and recommended action order using live task records.`}
                  />
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                Overdue means open for more than {WAREHOUSE_TASK_OVERDUE_DAYS} days.
              </p>
              <Link to="/warehouse/tasks" className="shrink-0 text-xs font-medium text-primary hover:underline">
                View all tasks
              </Link>
            </div>
          </CardContent>
        );
    }
  };

  const orderedDefinitions = draftLayout.orderedCardIds
    .map((id) => definitionById.get(id))
    .filter(Boolean) as WorkspaceCardDefinition[];
  const responsiveCardLayouts = useMemo(
    () =>
      ({
        lg: draftLayout.cardLayouts.lg.map((item) => ({
          ...item,
          minW: 2,
          minH: 6,
          maxH: 20,
        })),
        md: draftLayout.cardLayouts.md.map((item) => ({
          ...item,
          minW: 1,
          minH: 6,
          maxH: 20,
        })),
        sm: draftLayout.cardLayouts.sm.map((item) => ({
          ...item,
          minW: 1,
          maxW: 1,
          minH: 6,
          maxH: 20,
        })),
      }) satisfies ResponsiveLayouts<WorkspaceBreakpoint>,
    [draftLayout.cardLayouts]
  );

  return (
    <>
      <PageHeader
        className="sticky top-0 z-10 -mx-6 -mt-6 bg-background px-6 pt-3"
        title="My workspace"
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2 md:w-auto">
            {!editing && canViewGlobalInsights && (
              <a
                href="https://proscs.lognai.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={buttonStyles({
                  variant: 'secondary',
                  size: 'sm',
                  className: 'w-full justify-center whitespace-nowrap sm:w-auto',
                })}
              >
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>Supply Chain Sentinel</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
              </a>
            )}
            {!editing && canViewShipments && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full justify-center whitespace-nowrap sm:w-auto"
                onClick={() => navigate('/command-center')}
              >
                <Radar className="h-4 w-4 shrink-0" />
                <span>Global Command Center</span>
              </Button>
            )}
            {editing ? (
                <>
                  <Button variant="ghost" size="sm" onClick={resetDraft}>
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </Button>
                  <Button variant="secondary" size="sm" onClick={cancelEditing}>
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveLayout}
                    isLoading={savePreferences.isPending}
                    disabled={!unsaved}
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </>
              ) : (
                <IconButton
                  icon={Pencil}
                  aria-label="Customize layout"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditing(true);
                    setSaveError(null);
                  }}
                  disabled={mobile || !activeCompanyId}
                  title={
                    mobile
                      ? 'Layout editing is available on tablet and desktop'
                      : 'Customize layout'
                  }
                />
              )}
          </div>
        }
      />

      <PageLayout className="space-y-5 pt-4">
        {focusItems.length > 0 && (
          <MetricRibbon
            role="region"
            aria-label="Operational focus"
            items={focusItems.map((item) => {
              const Icon = item.icon;
              return {
                id: item.id,
                label: item.label,
                icon: <Icon />,
                // A null value means that KPI's query is still in flight; the
                // strip as a whole is not loading, so skeleton just this cell.
                value:
                  item.value === null ? (
                    <span className="block h-6 w-20 animate-pulse rounded bg-muted" />
                  ) : (
                    item.value
                  ),
                hint: item.warning ? (
                  <span className="text-warning-foreground">{item.hint}</span>
                ) : (
                  item.hint
                ),
                onClick: () => scrollToCard(item.target),
              };
            })}
          />
        )}

        {editing && (
          // Sticky so Save/Cancel and Add chart stay reachable while dragging
          // cards far down the grid. `top-4` leaves a gap above it rather than
          // letting it butt against the header; opaque, or the cards scrolling
          // underneath show through.
          <div className="sticky top-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/25 bg-primary-subtle px-4 py-3 shadow-md">
            <div>
              <p className="text-sm font-medium text-foreground">Customizing your Workspace</p>
              <p className="text-xs text-muted-foreground">
                Drag card headers, resize from any corner, or use each card menu.
                Changes are personal to this company.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {unsaved && (
                <Badge tone="warning" variant="subtle" size="sm">
                  Unsaved changes
                </Badge>
              )}
              {canAddCharts && (
                <Button variant="primary" size="sm" onClick={() => setChartPickerOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Add chart
                </Button>
              )}
            </div>
          </div>
        )}

        {(loadFailed || saveError) && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3"
          >
            <div className="flex min-w-0 items-start gap-3">
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {saveError ? 'Layout could not be saved' : 'Saved layout could not be loaded'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {saveError ?? 'The default layout is shown as unsaved. Your server preference was not replaced.'}
                </p>
              </div>
            </div>
            {loadFailed && !saveError && (
              <Button variant="secondary" size="sm" onClick={() => preferenceQuery.refetch()}>
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            )}
            {saveError && (
              <Button variant="secondary" size="sm" onClick={saveLayout}>
                <RefreshCw className="h-4 w-4" />
                Retry save
              </Button>
            )}
          </div>
        )}

        <ChartCardPicker
          open={chartPickerOpen}
          onOpenChange={setChartPickerOpen}
          onSelect={addChartCard}
          alreadyAdded={
            new Set(
              draftLayout.orderedCardIds.filter(isChartCardId).map(visualIdFromCardId)
            )
          }
        />

        <LatestInsightCard
          limit={5}
          onNavigate={(url) => navigate(url)}
          onViewAll={() => navigate('/insights')}
        />

        {!layoutReady ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            {Array.from({ length: Math.max(visibleDefinitions.length, 3) }).map((_, index) => (
              <Card key={index} className="min-h-56 md:col-span-1 xl:col-span-2">
                <CardSkeleton />
              </Card>
            ))}
          </div>
        ) : orderedDefinitions.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                title="No operational cards available"
                description="Your current company and permissions do not expose any Workspace modules."
              />
            </CardContent>
          </Card>
        ) : (
          <section
            ref={gridContainerRef}
            aria-label="Workspace briefing board"
            className="min-h-[420px] min-w-0 overflow-hidden"
          >
            {gridWidth > 0 && (
              <Responsive
                className={
                  editing && !mobile
                    ? 'workspace-grid workspace-grid--editing'
                    : 'workspace-grid'
                }
                width={gridWidth}
                layouts={responsiveCardLayouts}
                breakpoints={WORKSPACE_BREAKPOINTS}
                cols={WORKSPACE_COLUMNS}
                rowHeight={40}
                margin={[12, 12]}
                containerPadding={[0, 0]}
                compactor={verticalCompactor}
                dragConfig={{
                  enabled: editing && !mobile,
                  bounded: true,
                  handle: '.workspace-drag-handle',
                  cancel: 'button,input,select,textarea,a,[role="menu"]',
                  threshold: 3,
                }}
                resizeConfig={{
                  enabled: editing && !mobile,
                  handles: ['nw', 'ne', 'sw', 'se'],
                }}
                onBreakpointChange={(breakpoint) => {
                  setActiveBreakpoint(breakpoint as WorkspaceBreakpoint);
                }}
                onLayoutChange={(_layout, nextLayouts) => {
                  if (!editing || mobile) return;
                  setDraftLayout((current) =>
                    applyWorkspaceGridLayouts(
                      current,
                      nextLayouts as ResponsiveLayouts<WorkspaceBreakpoint>,
                      activeBreakpoint
                    )
                  );
                  setSaveError(null);
                }}
              >
                {orderedDefinitions.map((definition, index) => (
                  <div
                    key={definition.id}
                    className="workspace-card-container min-h-0 min-w-0"
                  >
                    <WorkspaceCardShell
                      definition={definition}
                      editing={editing && !mobile}
                      hideHeader={definition.id === 'radar'}
                      pinned={draftLayout.pinnedCardIds.includes(definition.id)}
                      position={index}
                      total={orderedDefinitions.length}
                      onMove={moveCard}
                      onTogglePin={(id) => {
                        setDraftLayout(toggleWorkspaceCardPin(draftLayout, id));
                        setSaveError(null);
                      }}
                      onRemove={isChartCardId(definition.id) ? removeCard : undefined}
                    >
                      {renderCardBody(definition.id)}
                    </WorkspaceCardShell>
                  </div>
                ))}
              </Responsive>
            )}
          </section>
        )}
      </PageLayout>
    </>
  );
}
